import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Sparkles, LockKeyhole } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTheme } from '../zustand/ThemeStore';
import { useUserStore } from '../zustand/UserStore';
import { useHierarchicalUsersStore } from '../zustand/HierarchicalUsersStore';
import { useAuthorization } from '../hooks/useAuthorization';
import UserSelector from '../components/analysis/UserSelector';
import type { UserType } from '../components/analysis/UserSelector';
import FileList from '../components/analysis/FileList';
import FileUploadArea from '../components/analysis/FileUploadArea';
import AnalysisLoader from '../components/analysis/AnalysisLoader';
import { getApiBaseUrl } from '../utils/config';

const AnalysisDashboard = () => {
  const navigate = useNavigate();

  type UploadedFile = {
    id: number;
    name: string;
    size: string;
    type: 'audio' | 'text';
    duration?: number;
    waveform?: number[];
    file: File;
  };

  const isDarkMode = useTheme((set) => set.theme);
  const currentUser = useUserStore((state) => state.user);
  const auth = useAuthorization();
  const [date, setDate] = useState<string | null>(null);
  
  // Use hierarchical users store instead of local state
  const { users, isLoading: usersLoading, error: usersError, fetchUsers } = useHierarchicalUsersStore();
  
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [playingId, setPlayingId] = useState<number | null>(null);
  const [userSearch, setUserSearch] = useState<string>('');
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Hierarchical user filtering logic
  const getVisibleUsers = () => {
    if (!currentUser || !Array.isArray(users)) return [];

    // Filter users based on search query first
    const filteredUsers = users.filter(user =>
      user.name.toLowerCase().includes(userSearch.toLowerCase()) ||
      user.role.toLowerCase().includes(userSearch.toLowerCase()) ||
      user.email.toLowerCase().includes(userSearch.toLowerCase())
    );

    // Defensive: role/designation may be undefined/null
    // const userRole = typeof currentUser.role === 'string' ? currentUser.role.toLowerCase().trim() : '';
    // const userDesignation = typeof currentUser.designation === 'string' ? currentUser.designation.toLowerCase().trim() : '';

    // // If Super Admin (check multiple variations), show hierarchical users
    // if (userRole.includes('super') || userDesignation.includes('super') ||
    //     userRole === 'super admin' || userDesignation === 'super admin') {
      
    //   // Find all users directly created by this Super Admin (including Admins)
    //   const directlyCreatedUsers = filteredUsers.filter(u => u.head === currentUser.uid);
      
    //   // Find Admins created by this Super Admin
    //   const createdAdmins = directlyCreatedUsers.filter(u => {
    //     const uRole = typeof u.role === 'string' ? u.role.toLowerCase().trim() : '';
    //     return uRole.includes('admin') && !uRole.includes('super');
    //   });
      
    //   // Find users created by the Admins that this Super Admin created
    //   const usersCreatedByMyAdmins = filteredUsers.filter(u => 
    //     createdAdmins.some(admin => admin.uid === u.head)
    //   );
 
    //   const allVisibleUsers = [...directlyCreatedUsers, ...usersCreatedByMyAdmins];
      
    //   // Remove duplicates and exclude the current user
    //   const uniqueUsers = allVisibleUsers.filter((u, index, self) => 
    //     index === self.findIndex(user => user.uid === u.uid) && u.uid !== currentUser.uid
    //   );
      
    //   return uniqueUsers;
    // }

    // // If Admin (check multiple variations), only show users they created
    // if (userRole.includes('admin') || userDesignation.includes('admin') ||
    //     userRole === 'admin' || userDesignation === 'admin') {
    //   const adminUsers = filteredUsers.filter(u => {
    //     const uRole = typeof u.role === 'string' ? u.role.toLowerCase().trim() : '';
    //     // Show users created by this admin, excluding themselves
    //     return u.head === currentUser.uid && u.uid !== currentUser.uid &&
    //            (uRole === 'user' || uRole === 'representative' ||
    //             (!uRole.includes('admin') && !uRole.includes('super')));
    //   });
      
    //   return adminUsers;
    // }

    // Regular Users - don't show any users for selection
    return filteredUsers;
  };

  const visibleUsers = getVisibleUsers();

  // Fetch users from hierarchical store on mount
  useEffect(() => {
    if (currentUser) {
      fetchUsers();
    }
  }, [currentUser, fetchUsers]);

  // Auto-select current user for regular users (non-admin)
  useEffect(() => {
    if (currentUser && !auth.canAccessAnalysisDashboard && !selectedUser) {
      // For regular users, automatically set them as the selected user
      const currentUserAsType: UserType = {
        uid: currentUser.uid,
        name: currentUser.name,
        email: currentUser.email,
        role: currentUser.role,
        status: (currentUser.status as 'suspended' | 'unsuspended') || 'unsuspended',
        designation: currentUser.designation || '',
        head: currentUser.head || '',
        created_at: new Date().toISOString(),
        email_verified: true
      };
      setSelectedUser(currentUserAsType);
    }
  }, [currentUser, auth.canAccessAnalysisDashboard, selectedUser]);

  const handleFiles = (files: FileList | null) => {
    if (!files || !selectedUser) return;

    const newFiles: UploadedFile[] = Array.from(files)
      .filter((file: File) => {
        const isAudio = file.type.startsWith('audio/');
        const isText = file.type.startsWith('text/') || 
                      file.name.endsWith('.txt') || 
                      file.name.endsWith('.srt') || 
                      file.name.endsWith('.vtt');
        return isAudio || isText;
      })
      .map((file: File) => {
        const isAudio = file.type.startsWith('audio/');
        return {
          id: Date.now() + Math.random(),
          name: file.name,
          size: (file.size / 1024 / 1024).toFixed(2) + ' MB',
          type: isAudio ? 'audio' : 'text',
          ...(isAudio && {
            duration: Math.floor(Math.random() * 300) + 60,
            waveform: generateMockWaveform()
          }),
          file: file,
        };
      });
    setUploadedFiles((prev: UploadedFile[]) => [...prev, ...newFiles]);
  };

  const generateMockWaveform = (): number[] => {
    return Array.from({ length: 30 }, () => Math.random() * 0.8 + 0.2);
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleUserSelect = (user: UserType) => {
    if (selectedUser && selectedUser.uid !== user.uid) {
      setUploadedFiles([]);
    }
    setSelectedUser(user);
  };

  const handleUserDeselect = () => {
    setSelectedUser(null);
    setUploadedFiles([]);
  };


  const handleAnalyze = async () => {
    if (!selectedUser || uploadedFiles.length === 0) return;

    setIsAnalyzing(true);
    try {
      // Prepare FormData for multipart/form-data (same as before)
      const formData = new FormData();
      formData.append('representative_name', selectedUser.name);
      formData.append('id', String(selectedUser.uid));
      if (date) {
        formData.append('date', date);
      }

      // Separate audio and transcript files
      const audioFiles = uploadedFiles.filter(f => f.type === 'audio');
      const transcriptFiles = uploadedFiles.filter(f => f.type === 'text');

      audioFiles.forEach((f) => {
        formData.append('audio_files', f.file, f.name);
      });
      transcriptFiles.forEach((f) => {
        formData.append('transcript_files', f.file, f.name);
      });

      try {
        // POST request to start background job (modified endpoint response)
        const res = await axios.post(`${getApiBaseUrl()}/api/v1/analyze`, formData, {
          headers: { 'Content-Type': 'multipart/form-data',Authorization: `Bearer ${localStorage.getItem('token')}` },
        });

        const { job_id, status, message } = res.data;
        console.log(status)

        // Stop loading immediately - job is now running in background
        setIsAnalyzing(false);
        
        // Show success toast with job info
        toast.success(
          `🚀 ${message || 'Analysis started!'}`,
          {
            description: `Job ID: ${job_id.slice(0, 8)}... - Tracking ${uploadedFiles.length} files`
          }
        );

        // Start monitoring the job progress via SSE
        startJobMonitoring(job_id);

        // Clear the form
        setSelectedUser(null);
        setUploadedFiles([]);
        setDate(null);
        
        // Optional: Navigate to analysis dashboard
        navigate('/analysis');

      } catch (error: any) {
        setIsAnalyzing(false);
        console.error('Analysis failed:', error);
        
        const errorMessage = error?.response?.data?.detail || 'Analysis job failed to start';
        toast.error('❌ Analysis Failed', {
          description: errorMessage
        });
      }
    } catch (error) {
      setIsAnalyzing(false);
      console.error('Unexpected error:', error);
      toast.error('❌ Unexpected Error', {
        description: 'Something went wrong. Please try again.'
      });
    }
  };

  // New function to monitor job progress via Server-Sent Events
  const startJobMonitoring = (jobId: string) => {
    const eventSource = new EventSource(`${getApiBaseUrl()}/api/v1/jobs/${jobId}/stream?user_id=${selectedUser?.uid}`);
    const progressToastId = `progress-${jobId}`;
    
    const getStepIcon = (step: string) => {
      switch (step) {
        case 'processing_files':
          return '📁';
        case 'generating_transcript':
          return '📝';
        case 'analyzing_call':
          return '🔍';
        default:
          return '⏳';
      }
    };
    
    const getStepLabel = (step: string) => {
      switch (step) {
        case 'processing_files':
          return 'Step 1/3: Processing Files';
        case 'generating_transcript':
          return 'Step 2/3: Generating Transcript';
        case 'analyzing_call':
          return 'Step 3/3: Analyzing Call';
        default:
          return 'Processing...';
      }
    };
    
    eventSource.addEventListener('progress', (e) => {
      const data = JSON.parse(e.data);
      
      console.log('Progress update received:', data); // Debug log
      
      const stepIcon = getStepIcon(data.progress_step);
      const stepLabel = getStepLabel(data.progress_step);
      
      // Update the same toast instead of creating new ones
      toast.info(`${stepIcon} ${stepLabel}`, {
        id: progressToastId, // Use same ID to update existing toast
        description: data.progress_message || `Processing ${data.processed_files}/${data.total_files} files`,
        duration: 5000 // Show for 5 seconds
      });
    });
    
    eventSource.addEventListener('complete', (e) => {
      eventSource.close();
      const data = JSON.parse(e.data);
      
      // Dismiss the progress toast
      toast.dismiss(progressToastId);
      
      toast.success(
        '✅ Analysis Complete!', 
        {
          description: 'Click to view detailed results',
          action: {
            label: 'View Results',
            onClick: () => {
              navigate(`/job-results/${data.job_id}`);
            }
          },
          duration: Infinity,
        }
      );
    });
    
    eventSource.addEventListener('error', (e) => {
      eventSource.close();
      console.error('SSE Error:', e);
      
      // Dismiss the progress toast
      toast.dismiss(progressToastId);
      
      // Fallback: show generic completion message
      toast.warning('⚠️ Analysis Status Unknown', {
        description: 'Please check your results manually',
        action: {
          label: 'Check Results',
          onClick: () => navigate('/analysis')
        }
      });
    });
    
    eventSource.onerror = () => {
      eventSource.close();
      // Dismiss the progress toast
      toast.dismiss(progressToastId);
      // Silent fallback - user can check results manually
    };
  };

  const canUpload = selectedUser !== null;
  const canAnalyze = selectedUser !== null && uploadedFiles.length > 0;

  const audioFiles = uploadedFiles.filter(f => f.type === 'audio');
  const textFiles = uploadedFiles.filter(f => f.type === 'text');

  // Show loader for analysis
  if (isAnalyzing) {
    return <AnalysisLoader />;
  }

  // Show loader for users
  if (usersLoading) {
    return (
      <div className={`${isDarkMode ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'} flex items-center justify-center h-screen`}>
        <div className="text-center text-gray-400">Loading users...</div>
      </div>
    );
  }

  if (usersError) {
    return (
      <div className={`${isDarkMode ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'} flex items-center justify-center h-screen`}>
        <div className="text-center text-red-500">{usersError}</div>
      </div>
    );
  }
  // Main dashboard
  return (
    <div className={`${isDarkMode ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'} min-h-screen`}>
      <div className="flex flex-col p-6 pb-0 h-fit">
        <div className="relative h-[calc(100vh-7rem)] overflow-hidden">
          {/* User Selection Panel - Covers full screen when no user selected */}
          {auth.canAccessAnalysisDashboard && (
            <div 
              className={`absolute inset-0 transition-all duration-700 ease-in-out ${
                selectedUser 
                  ? 'w-[41.666667%] opacity-100' // col-span-5 equivalent (5/12 = 41.666667%)
                  : 'w-full opacity-100'
              }`}
              style={{
                zIndex: selectedUser ? 1 : 10
              }}
            >
              <div className="h-full pr-6">
                <UserSelector
                  users={visibleUsers}
                  selectedUser={selectedUser}
                  onSelect={handleUserSelect}
                  onDeselect={handleUserDeselect}
                  userSearch={userSearch}
                  setUserSearch={setUserSearch}
                />
              </div>
            </div>
          )}
          
          {/* Right Column - Files Section */}
          <div 
            className={`absolute inset-0 transition-all duration-700 ease-in-out ${
              auth.canAccessAnalysisDashboard 
                ? selectedUser
                  ? 'left-[calc(41.666667%+1.5rem)] opacity-100 pointer-events-auto' // Show after user selected
                  : 'left-full opacity-0 pointer-events-none' // Hidden off-screen
                : 'left-0 opacity-100 pointer-events-auto' // Full width for non-admins
            }`}
            style={{
              zIndex: 2
            }}
          >
            <div className="flex flex-col gap-6 h-full">
            {/* Combined Upload & Files Area */}
            <div className={`rounded-2xl p-5 flex-1 flex flex-col h-full border ${
              isDarkMode 
                ? 'bg-[#0a1414] border-[#1a2424]' 
                : 'bg-white border-gray-200'
            }`}>
              {/* Date Picker UI */}
              <div className="mb-4 flex items-center gap-3">
                <label htmlFor="analysis-date" className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Analysis Date:</label>
                <input
                  id="analysis-date"
                  type="date"
                  value={date || ''}
                  onChange={e => setDate(e.target.value)}
                  className={`rounded px-2 py-1 border focus:outline-none focus:ring-2 ${
                    isDarkMode
                      ? 'bg-[#1f3c3c] border-[#1a2424] text-white focus:ring-[#1a9f6b]'
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-[#1a9f6b]'
                  }`}
                  max={new Date().toISOString().split('T')[0]}
                  disabled={!canUpload}
                />
              </div>
              {/* Hidden file input - always in DOM */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="audio/*,text/*,.txt,.srt,.vtt"
                onChange={(e) => handleFiles(e.target.files)}
                disabled={!canUpload}
                className="hidden"
              />
              <div className="flex items-center justify-between mb-4 flex-shrink-0">
                <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}> 
                  Files {uploadedFiles.length > 0 && `(${uploadedFiles.length})`}
                  {uploadedFiles.length > 0 && (
                    <span className={`text-sm font-normal ml-2 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {audioFiles.length} audio, {textFiles.length} text
                    </span>
                  )}
                </h2>
                <div className="flex items-center gap-3">
                  {!canUpload && uploadedFiles.length === 0 && (
                    <div className={`flex items-center gap-2 text-xs ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      <LockKeyhole className="w-3 h-3 text-gray-400" />
                      Select user first
                    </div>
                  )}
                  {uploadedFiles.length > 0 && (
                    <button
                      onClick={() => setUploadedFiles([])}
                      className={`text-xs px-3 py-1.5 rounded-full transition-all ${
                        isDarkMode 
                          ? 'bg-[#050a0a] hover:bg-[#0f1919] text-white' 
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                    >
                      Clear All
                    </button>
                  )}
                </div>
              </div>
              {uploadedFiles.length === 0 ? (
                <FileUploadArea
                  canUpload={canUpload}
                  isDragging={isDragging}
                  setIsDragging={setIsDragging}
                  fileInputRef={fileInputRef}
                  handleFiles={handleFiles}
                />
              ) : (
                <FileList
                  uploadedFiles={uploadedFiles}
                  playingId={playingId}
                  setPlayingId={setPlayingId}
                  setUploadedFiles={setUploadedFiles}
                  formatTime={formatTime}
                  fileInputRef={fileInputRef}
                  canUpload={canUpload}
                  handleFiles={handleFiles}
                />
              )}
            </div>
              {/* Analyze Button - Always Visible */}
              <div className="flex-shrink-0 mb-1">
                <button
                  onClick={handleAnalyze}
                  disabled={!canAnalyze || isAnalyzing}
                  className={`w-full py-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-3 ${
                    canAnalyze && !isAnalyzing
                      ? 'bg-gradient-to-r from-[#1a9f6b] to-[#148856] hover:from-[#148856] hover:to-[#0f6644] text-white shadow-lg shadow-[#1a9f6b]/30 cursor-pointer'
                      : `${isDarkMode ? 'bg-[#050a0a] text-gray-500' : 'bg-gray-100 text-gray-400'} cursor-not-allowed`
                  }`}
                >
                  <Sparkles className="w-5 h-5" />
                  Analyze Files
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AnalysisDashboard;